/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { KeyNumber } from 'qp-navigation';
import { ThumbnailData } from 'src/app/shared/models/ThumbnailData';
import { CuepointGroup } from '../../shared/models/PromoGroup';
import { Thumbnails } from '../interfaces/thumbnails.interface';
import { PlayerSeekbar } from './player-seekbar.interface';

@Component({
  selector: 'app-player-seekbar',
  templateUrl: './player-seekbar.component.html',
  styleUrls: ['./player-seekbar.component.scss'],
})
export class PlayerSeekbarComponent implements OnInit, OnDestroy {
  @ViewChild('bar')
  bar: ElementRef;

  @Output() isSeeking = new EventEmitter<PlayerSeekbar>();
  @Output() endSeek = new EventEmitter<PlayerSeekbar>();
  @Output() enter = new EventEmitter<PlayerSeekbar>();
  @Input() max: number;
  @Input() value: number;
  @Input() step: number;
  @Input() isLive: boolean;
  @Input() cuepoints: CuepointGroup[];
  @Input() isAd = false;
  @Input() keyframesEnabled = false;
  @Input() showCuepoints = false;
  @Input() thumbnails: Thumbnails;
  @Input() automaticSeek: boolean;
  @Input() blockControls = false;
  @Input() percentage = 0;

  currentThumbnail: ThumbnailData;
  seeking = false;
  seekingWithMouse = false;
  innerValue: number;

  private click: () => void;
  private mouseup: () => void;
  private mousemove: () => void;
  private offsetLeft: number;

  // Virtual Seek behaviour
  // private seekTimeout: any = null;
  private seekInterval: any = null;
  private lastKeyTime = 0;
  private virtualValue = 0;

  constructor(private renderer: Renderer2, private el: ElementRef) {}

  ngOnInit(): void {
    this.handleClick();
    this.handleMouseMove();
    this.handleMouseUp();
  }

  onKeyDown(event: any): void {
    // Controls are blocked when the player is in an ad state or buffering
    if (this.blockControls) return;
    if ([KeyNumber.LEFT, KeyNumber.RIGHT].includes(event.keyCode)) {
      this.lastKeyTime = Date.now();

      if (!this.seeking) {
        this.seeking = true;
        this.virtualValue = this.value;
        this.startSeekLoop(event);
      }
    } else if (KeyNumber.ENTER === event.keyCode) {
      this.enter.emit();
    }
  }

  startSeekLoop(event: any): void {
    this.seekInterval = setInterval(() => {
      const now = Date.now();

      if (now - this.lastKeyTime > 500) {
        this.stopSeeking(); // No keydown recently
        return;
      }

      // Simulate one step per tick
      const sign = KeyNumber.RIGHT === event.keyCode ? +1 : -1;
      let newValue = this.virtualValue + sign * this.step;
      if (newValue < 0) {
        newValue = 0;
      } else if (newValue > this.max - 1) {
        newValue = this.max - 1;
      }
      this.virtualValue = newValue;
      this.innerValue = newValue;

      this.isSeeking.emit({ value: newValue, keyCode: event.keyCode });
      this.handleThumbnail();
    }, 150);
  }

  stopSeeking(): void {
    if (this.seekInterval) {
      clearInterval(this.seekInterval);
      this.seekInterval = null;
    }

    this.handleChange();
    this.seeking = false;
  }

  onMouseDown(): void {
    this.renderer.addClass(this.el.nativeElement, 'transition');
    this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
    this.seekingWithMouse = true;
    this.isSeeking.emit({ value: this.value });
  }

  onFocusOut() {
    if (this.seeking) {
      this.handleChange();
    }
  }

  ngOnDestroy() {
    this.click();
    this.mouseup();
    this.mousemove();
  }

  private handleMouseUp(): void {
    this.mouseup = this.renderer.listen('document', 'mouseup', () => {
      if (this.seekingWithMouse) {
        this.seekingWithMouse = false;
        this.renderer.removeClass(this.el.nativeElement, 'transition');
        this.endSeek.emit({ value: this.value });
      }
    });
  }

  private handleMouseMove(): void {
    this.mousemove = this.renderer.listen('document', 'mousemove', (e) => {
      if (this.seekingWithMouse) {
        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;
        if (value < 0) {
          this.value = 0;
        } else if (value > this.max) {
          this.value = this.max;
        } else {
          this.value = value;
        }
        this.isSeeking.emit({ value: this.value });
        this.handleThumbnail();
      }
    });
  }

  private async handleThumbnail() {
    if (!this.keyframesEnabled) return;
    if (this.thumbnails?.content?.length > 0) {
      this.currentThumbnail = this.thumbnails.content.reduce((prev, curr) =>
        Math.abs(curr.id - this.innerValue) < Math.abs(prev.id - this.innerValue) ? curr : prev
      );
    }
  }

  private handleClick(): void {
    this.click = this.renderer.listen(this.el.nativeElement, 'click', (e) => {
      const className = e?.srcElement?.className || '';
      if (!className.includes('button')) {
        this.offsetLeft = this.getOffsetLeft(this.el.nativeElement);
        const x = e.clientX - this.offsetLeft;
        const width = this.bar.nativeElement.offsetWidth;
        const value = (x / width) * this.max;
        this.value = value;
        this.endSeek.emit({ value });
      }
    });
  }

  private handleChange(): void {
    this.endSeek.emit({ value: this.innerValue });
    this.seeking = false;
    this.renderer.removeClass(this.el.nativeElement, 'transition');
  }

  private getOffsetLeft(el: any): number {
    if (el?.offsetParent) {
      return el.offsetLeft + (this.getOffsetLeft(el.offsetParent) || 0);
    } else {
      return el.offsetLeft || 0;
    }
  }

  // private updateSeekbarPosition(newValue: number) {
  //   if (this.animationFrameId) {
  //     cancelAnimationFrame(this.animationFrameId);
  //   }

  //   this.animationFrameId = requestAnimationFrame(() => {
  //     this.innerValue = newValue;
  //     this.hideSeekDot = false;
  //     this.animationFrameId = null;
  //   });
  // }
}
